import { useCallback, useEffect, useState } from "react";
import { MessageCircle, Printer } from "lucide-react";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { brl, dateTime, PAYMENT, STATUS } from "@/lib/format";
import { printOrder } from "@/lib/printer";
import { canNotify, notifyWhatsapp } from "@/lib/whatsapp";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const FILTERS = ["", "pendente", "aguardando_comprovante", "preparando", "saiu", "concluido", "cancelado"];

const OrderCard = ({ order, onStatus, onPrint, onDelete, isOwner, pixKey }) => (
  <div data-testid={`order-card-${order.number}`} className={`rounded-2xl border bg-[#1A1A1A] p-5 ${order.status === "pendente" ? "border-primo-500/40" : "border-white/[0.08]"}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <p className="font-display text-2xl font-bold">#{order.number} <span className="text-base font-medium text-slate-400">{order.customer_name}</span></p>
        <p className="text-sm text-slate-500">{dateTime(order.created_at)} · {order.phone} · {order.fulfillment === "entrega" ? "Entrega" : "Retirada"}</p>
        {order.fulfillment === "entrega" && <p className="text-sm text-slate-300">{order.address}</p>}
        {order.notes && <p className="text-sm text-primo-300">Obs.: {order.notes}</p>}
      </div>
      <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${STATUS[order.status].cls} ${order.status === "pendente" ? "animate-pulse" : ""}`}>{STATUS[order.status].label}</span>
    </div>
    <div className="mt-3 space-y-1 text-sm text-slate-300">
      {order.items.map((i, idx) => (
        <div key={`${i.product_id}-${idx}`}>
          <div className="flex justify-between"><span>{i.qty}x {i.name}</span><span>{brl(i.subtotal)}</span></div>
          {(i.selections || []).length > 0 && (
            <p className="pl-4 text-xs text-primo-300">{i.selections.map((s) => `${s.qty}x ${s.option}${s.price ? ` (+${brl(s.price)})` : ""}`).join(", ")}</p>
          )}
        </div>
      ))}
    </div>
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-white/[0.08] pt-3">
      <p className="text-sm"><span className="font-display text-lg font-bold text-primo-400">{brl(order.total)}</span> <span className="text-slate-400">· {PAYMENT[order.payment_method]}{order.change_for ? ` (troco p/ ${brl(order.change_for)})` : ""}</span>
        {order.coupon_code && <span data-testid={`order-coupon-${order.number}`} className="ml-2 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-semibold text-emerald-400">{order.coupon_code} -{brl(order.discount)}{order.coupon_partner ? ` · ${order.coupon_partner}` : ""}</span>}
      </p>
      <div className="flex gap-2">
        <Select value={order.status} onValueChange={(v) => onStatus(order, v)} disabled={order.status === "cancelado"}>
          <SelectTrigger data-testid={`order-status-select-${order.number}`} className="h-9 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS).map(([k, s]) => <SelectItem key={k} value={k} data-testid={`status-option-${k}`}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <button data-testid={`print-receipt-button-${order.number}`} onClick={() => onPrint(order)}
          className="flex h-9 items-center gap-2 rounded-lg bg-primo-500 px-3 text-sm font-semibold text-white hover:bg-primo-400"><Printer className="h-4 w-4" /> Imprimir</button>
        {canNotify(order.status) && (
          <button data-testid={`whatsapp-button-${order.number}`} onClick={() => notifyWhatsapp({ ...order, pix_key: pixKey })}
            className="flex h-9 items-center gap-2 rounded-lg bg-emerald-600 px-3 text-sm font-semibold text-white hover:bg-emerald-500"><MessageCircle className="h-4 w-4" /> Avisar</button>
        )}
        {order.payment_method === "pix" && order.status === "pendente" && (
          <button data-testid={`request-receipt-button-${order.number}`} onClick={() => onStatus(order, "aguardando_comprovante")}
            className="flex h-9 items-center gap-2 rounded-lg bg-yellow-500 px-3 text-sm font-semibold text-black hover:bg-yellow-400"><MessageCircle className="h-4 w-4" /> Cobrar comprovante</button>
        )}
        {isOwner && (
          <button data-testid={`delete-order-button-${order.number}`} onClick={() => onDelete(order)}
            className="flex h-9 items-center gap-2 rounded-lg bg-white/5 px-3 text-sm font-semibold text-red-400 hover:bg-red-500/20"><Trash2 className="h-4 w-4" /> Excluir</button>
        )}
      </div>
    </div>
  </div>
);

export default function Orders() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [filter, setFilter] = useState("");
  const [orders, setOrders] = useState([]);
  const [store, setStore] = useState(null);

  const load = useCallback(() => api.get("/admin/orders", { params: filter ? { status: filter } : {} }).then((r) => setOrders(r.data)), [filter]);

  useEffect(() => {
    load();
    api.get("/settings").then((r) => setStore(r.data));
    window.addEventListener("orders:refresh", load);
    return () => window.removeEventListener("orders:refresh", load);
  }, [load]);

  const onStatus = async (order, status) => {
    try {
      await api.patch(`/admin/orders/${order.id}/status`, { status });
      load();
      if (canNotify(status)) {
        toast.success(`Pedido #${order.number}: ${STATUS[status].label}`, {
          action: { label: "Avisar no WhatsApp", onClick: () => notifyWhatsapp({ ...order, status, pix_key: store?.pix_key }, status) },
          duration: 10000,
        });
      } else {
        toast.success(`Pedido #${order.number}: ${STATUS[status].label}`);
      }
    } catch (e) { toast.error(errMsg(e)); }
  };
  const onPrint = (order) => printOrder(order, store).catch((e) => toast.error(e.message || "Falha ao imprimir"));

  const onDelete = async (order) => {
    if (!window.confirm(`Excluir o pedido #${order.number} de ${order.customer_name}? Essa ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/orders/${order.id}`);
      toast.success(`Pedido #${order.number} excluído`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onDeleteAll = async () => {
    if (!window.confirm("Apagar TODOS os pedidos do histórico? Essa ação não pode ser desfeita.")) return;
    try {
      const { data } = await api.delete("/admin/orders/all");
      toast.success(`${data.deleted} pedido(s) apagado(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primo-500">Ao vivo · atualiza a cada 15s</p>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Pedidos</h1>
          {isOwner && orders.length > 0 && (
            <button data-testid="delete-all-orders-button" onClick={onDeleteAll}
              className="flex h-9 items-center gap-2 rounded-lg border border-red-500/30 px-3 text-sm font-semibold text-red-400 hover:bg-red-500/20">
              <Trash2 className="h-4 w-4" /> Apagar histórico
            </button>
          )}
        </div>
      </div>
      <div className="no-scrollbar flex gap-2 overflow-x-auto">
        {FILTERS.map((f) => (
          <button key={f || "todos"} data-testid={`orders-filter-${f || "todos"}`} onClick={() => setFilter(f)}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium ${filter === f ? "bg-primo-500 text-white" : "bg-white/5 text-slate-300"}`}>
            {f ? STATUS[f].label : "Todos"}
          </button>
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {orders.map((o) => <OrderCard key={o.id} order={o} onStatus={onStatus} onPrint={onPrint} onDelete={onDelete} isOwner={isOwner} pixKey={store?.pix_key} />)}
      </div>
      {orders.length === 0 && <p data-testid="orders-empty" className="py-12 text-center text-slate-500">Nenhum pedido aqui.</p>}
    </div>
  );
}
